import { useState, useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Link, withPrefix } from 'gatsby'
import { Accordion } from 'component-library'
import { Tooltip } from 'react-tooltip'
import copy from 'copy-to-clipboard'
import Popup from 'reactjs-popup'
import { useCheckBanner, sendCustomGAEvent } from 'shared-utilities'
import { useOnClickOutside } from 'usehooks-ts'
import useCloseOnEscape from '../../hooks/useCloseOnEscape'
import { useSiteMetadata } from '../../hooks/useSiteMetadata'
import Image from '../Image/Image'
import MarkdownText from '../MarkdownText/MarkdownText'
import LinkComponent from '../LinkComponent/LinkComponent'
import individual_demo from '../../content/individual_demo'
import './style.scss'

export interface ILinksListItem {
  url: string
  title: string
  items?: {
    url: string
    title: string
  }[]
}

interface IContentSidebarProps {
  linksList: ILinksListItem[]
  downloads?: { python?: string; notebook?: string; github?: string }
  related?: { link: string; label: string; slug: string; image?: string }[]
  BibTex?: string
  hardware?: Queries.Frontmatter['hardware']
  imagesWithoutPrefix?: boolean
  isLoadingRelatedContent?: boolean
  contentTitle: string
}

const demo_placeholder =
  'https://assets.cloud.pennylane.ai/pennylane_website/pages/demonstrations/demo_placeholder.png'

const isDesktopMargin = () => {
  const CUSTOM_WIDTH = 1199
  if (typeof window !== 'undefined') {
    return {
      isDesktopWidth: window.innerWidth > CUSTOM_WIDTH,
      width: window.innerWidth > CUSTOM_WIDTH,
      height: window.innerHeight,
    }
  }
}

const ContentSidebar = (props: IContentSidebarProps) => {
  const {
    linksList,
    downloads,
    related,
    BibTex,
    hardware,
    imagesWithoutPrefix,
    isLoadingRelatedContent,
    contentTitle,
  } = props
  const [isBannerPresent, setIsBannerPresent] = useState<boolean>(false)
  const [showSidebar, setShowSidebar] = useState(
    isDesktopMargin()?.isDesktopWidth ? true : false
  )
  const [windowHeight, setWindowHeight] = useState(0)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [isHardwareModalOpen, setIsHardwareModalOpen] = useState(false)
  const [hardwareModalContentId, setHardwareModalContentId] = useState('')
  const { user } = useAuth0()

  useCloseOnEscape(showSidebar, setShowSidebar)
  useCheckBanner(setIsBannerPresent)

  const ref = useRef(null)
  useOnClickOutside(ref, () => setShowSidebar(false))

  /** Sidebar is automatically visible on desktop,
   * but hidden on tablet and lower */
  useEffect(() => {
    setShowSidebar(isDesktopMargin()?.isDesktopWidth as boolean)
    setWindowHeight(isDesktopMargin()?.height as number)
  }, [])

  useEffect(() => {
    const handleWindowResize = () => {
      toggleBodyOverflowClass()
      setWindowHeight(isDesktopMargin()?.height as number)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  })

  useEffect(() => {
    toggleBodyOverflowClass()
  }, [showSidebar])

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfirmation(false)
    }, 2000)
    return () => clearTimeout(timer)
  }, [showConfirmation])

  const toggleBodyOverflowClass = (): void => {
    const bodyTag = document.querySelector('body')

    if (!isDesktopMargin()?.isDesktopWidth) {
      if (showSidebar) {
        !bodyTag?.classList.contains('Sidemenu--open') &&
          bodyTag?.classList.add('Sidemenu--open')
      } else {
        bodyTag?.classList.remove('Sidemenu--open')
      }
    } else {
      bodyTag?.classList.contains('Sidemenu--open') &&
        bodyTag?.classList.remove('Sidemenu--open')
    }
  }
  const closeSidebar = () => {
    if (!isDesktopMargin()?.isDesktopWidth) {
      setShowSidebar(false)
    }
  }

  const handleBibTeXButtonClick = async () => {
    BibTex && copy(BibTex)
    setShowConfirmation(true)
  }

  /** Renders LaTeX properly for table of contents titles */
  const getFormattedContentTitle = (title: string) => {
    return title.includes('\\') ? (
      <MarkdownText
        text={title.replaceAll('\\(', '$').replaceAll('\\)', '$')}
      />
    ) : (
      title
    )
  }
  const { siteUrl } = useSiteMetadata()

  return (
    <>
      <div
        className={`contentSidebar ${
          !showSidebar ? `contentSidebar--closed` : 'contentSidebar--open'
        }`}
        data-testid="content-sidebar"
      >
        <div
          className={`contentSidebar__container ${
            windowHeight <= 900
              ? isBannerPresent
                ? 'h-[calc(100vh-115px)]'
                : 'h-[calc(100vh-85px)]'
              : ''
          } ${!showSidebar ? `hidden` : ''}`}
          ref={!isDesktopMargin()?.isDesktopWidth ? ref : null}
        >
          {related && related.length > 0 && (
            <div
              className={`Sidebar__RelatedDemos ${
                related.length < 3 && !isLoadingRelatedContent
                  ? 'min-h-fit'
                  : 'min-h-[235px]'
              }`}
            >
              <div className="flex justify-between">
                <h3>Related materials</h3>
                <button
                  className="collapseMenu__button"
                  data-testid="collapse-sidebar-button"
                  onClick={() => setShowSidebar(false)}
                  aria-label="Collapse sidebar menu"
                >
                  <i className="bx bxs-arrow-from-left"></i>
                </button>
              </div>
              <ul>
                {related.map((relatedDemo, index) => {
                  return (
                    <li className="SidebarRelatedDemo" key={relatedDemo.link}>
                      <Link
                        to={relatedDemo.link}
                        data-testid={`related-link-${index}`}
                        onClick={() => closeSidebar()}
                      >
                        <Image
                          withoutPrefix={
                            imagesWithoutPrefix ||
                            (relatedDemo.image
                              ? !relatedDemo.image.includes(siteUrl)
                              : false)
                          }
                          src={
                            relatedDemo.image
                              ? relatedDemo.image
                              : demo_placeholder
                          }
                          alt=""
                          className="SidebarRelated__img"
                          data-testid="sidebar-demo-img"
                        />
                        <p>{relatedDemo.label}</p>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          <div className="SidebarContents">
            <h3>Contents</h3>
            <button
              className={`collapseMenu__button ${
                related && related.length > 0
                  ? 'collapseMenu__button--hide'
                  : ''
              } `}
              onClick={() => {
                setShowSidebar(false)
              }}
              aria-label="Collapse sidebar menu"
            >
              <i className="bx bxs-arrow-from-left"></i>
            </button>
          </div>
          <ul>
            {linksList.map((item: ILinksListItem, index) => (
              <li key={item.url} className="Contents__item">
                {item.items && item.items.length ? (
                  <>
                    <Accordion
                      key={`${item.title}-${index}`}
                      itemTitle={
                        <a
                          href={item.url}
                          data-testid={`toc-item-title-${index}`}
                        >
                          {getFormattedContentTitle(item.title)}
                        </a>
                      }
                      itemContent={
                        <ul>
                          {item.items.map(
                            (nestedListItem: ILinksListItem, nestedIndex) => (
                              <li key={nestedListItem.url}>
                                <LinkComponent
                                  href={nestedListItem.url}
                                  onClick={() => closeSidebar()}
                                  testId={`toc-item-accordion-nested-title-${nestedIndex}`}
                                >
                                  {getFormattedContentTitle(
                                    nestedListItem.title
                                  )}
                                </LinkComponent>
                              </li>
                            )
                          )}
                        </ul>
                      }
                    />
                  </>
                ) : (
                  <LinkComponent
                    href={item.url}
                    onClick={() => closeSidebar()}
                    testId={`toc-item-title-${index}`}
                  >
                    {getFormattedContentTitle(item.title)}
                  </LinkComponent>
                )}
              </li>
            ))}
          </ul>
          {hardware && hardware.length > 0 && (
            <>
              <div className="mt-8">
                <h3>Hardware</h3>
                <ul>
                  {hardware.map((vendor) => {
                    return (
                      <li key={vendor?.id}>
                        <button
                          data-testid={`hardware-button-${vendor?.id}`}
                          className="Hardware__button"
                          onClick={() => {
                            setHardwareModalContentId(vendor?.id || '')
                            setIsHardwareModalOpen(true)
                            sendCustomGAEvent('click', {
                              event_category: vendor?.id,
                              event_label: 'hardware-modal',
                              value: 'open-modal',
                            })
                          }}
                        >
                          <div className="w-[21.76px] mr-3">
                            <img
                              src={withPrefix(vendor?.logo || '')}
                              alt=""
                              className="max-w-full"
                              data-testid={`vendor-logo-${vendor?.id}`}
                            />
                          </div>{' '}
                          {
                            individual_demo.hardware[
                              vendor?.id as keyof typeof individual_demo.hardware
                            ].button_label
                          }
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
              <Popup
                open={isHardwareModalOpen}
                modal={true}
                lockScroll={true}
                onClose={() => {
                  setIsHardwareModalOpen(false)
                  setHardwareModalContentId('')
                }}
              >
                <div className="HardwareModal" data-testid="hardware-modal">
                  <div className="overflow-y-auto max-h-[90vh]">
                    <button
                      onClick={() => {
                        setIsHardwareModalOpen(false)
                        setHardwareModalContentId('')
                      }}
                      aria-label="close modal"
                      className="HardwareModal__close-button"
                    >
                      <i className="bx bx-x"></i>
                    </button>
                    {hardwareModalContentId.length > 0 && (
                      <div className="pt-8">
                        <MarkdownText
                          text={
                            individual_demo.hardware[
                              hardwareModalContentId as keyof typeof individual_demo.hardware
                            ].modal.description
                          }
                          className="HardwareModal__description"
                        />
                        <div className="text-roboto tracking-wide mb-4">
                          <a
                            href={
                              hardware.find(
                                (vendor) =>
                                  vendor?.id === hardwareModalContentId
                              )?.link
                            }
                            id="aws-braket-github"
                            onClick={() =>
                              sendCustomGAEvent('click', {
                                event_category: hardwareModalContentId,
                                event_label: 'hardware-modal',
                                value: 'github',
                              })
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="Button__primary--blue HardwareModal__cta-link mb-4"
                          >
                            {
                              individual_demo.hardware[
                                hardwareModalContentId as keyof typeof individual_demo.hardware
                              ].modal.github_link_text
                            }{' '}
                            <i className="bx bx-link-external"></i>
                          </a>
                          <a
                            href={
                              individual_demo.hardware[
                                hardwareModalContentId as keyof typeof individual_demo.hardware
                              ].modal.sign_up_link.url
                            }
                            id="aws-braket-signup"
                            onClick={() =>
                              sendCustomGAEvent('click', {
                                event_category: hardwareModalContentId,
                                event_label: 'hardware-modal',
                                value: 'signup',
                              })
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="Button__secondary--blue HardwareModal__cta-link"
                          >
                            {
                              individual_demo.hardware[
                                hardwareModalContentId as keyof typeof individual_demo.hardware
                              ].modal.sign_up_link.text
                            }{' '}
                            <i className="bx bx-link-external"></i>
                          </a>
                        </div>
                        <MarkdownText
                          text={
                            individual_demo.hardware[
                              hardwareModalContentId as keyof typeof individual_demo.hardware
                            ].modal.disclaimer
                          }
                          className="HardwareModal__disclaimer"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Popup>
            </>
          )}
          {downloads &&
            (downloads.python ||
              downloads.notebook ||
              downloads.github ||
              BibTex) && (
              <div className="mt-8">
                <h3>Downloads</h3>
                <ul>
                  {downloads.python && (
                    <li className="Download__link">
                      <a
                        href={downloads.python}
                        data-testid="python-download"
                        onClick={() =>
                          sendCustomGAEvent('download_python', {
                            event_category: 'demo',
                            event_label: contentTitle,
                            value: JSON.stringify({
                              id: user?.sub || '',
                            }),
                          })
                        }
                        download
                      >
                        <i className="bx bxl-python"></i> Download Python script
                      </a>
                    </li>
                  )}
                  {downloads.notebook && (
                    <li className="Download__link">
                      <a
                        href={downloads.notebook}
                        data-testid="notebook-download"
                        onClick={() =>
                          sendCustomGAEvent('download_notebook', {
                            event_category: 'demo',
                            event_label: contentTitle,
                            value: JSON.stringify({
                              id: user?.sub || '',
                            }),
                          })
                        }
                        download
                      >
                        <i className="bx bxs-download text-[19px]"></i>
                        Download Notebook
                      </a>
                    </li>
                  )}
                  {downloads.github && (
                    <li className="Download__link">
                      <a
                        href={downloads.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="github-link"
                      >
                        <i className="bx bxl-github"></i> View on GitHub
                      </a>
                    </li>
                  )}
                  {BibTex && (
                    <li className="Download__link">
                      <button
                        onClick={handleBibTeXButtonClick}
                        aria-label="Copy BibTeX citation"
                        className="BibTex__button"
                        data-testid="bibtex-button"
                        data-tooltip-id="bibtex-button"
                      >
                        <i className="bx bxs-book"></i> Copy BibTeX citation
                      </button>
                      <Tooltip
                        id="bibtex-button"
                        data-testid="bibtex-button-tooltip"
                        content="BibTeX copied"
                        isOpen={showConfirmation}
                        className="Bibtex__tooltip"
                      />
                    </li>
                  )}
                </ul>
              </div>
            )}
        </div>
      </div>
      <button
        className={`showSidebar__button ${!showSidebar && `block`}`}
        onClick={() => {
          setShowSidebar(true)
        }}
        aria-label="Open sidebar menu"
      >
        <i className="bx bx-left-indent"></i>
      </button>
    </>
  )
}

export default ContentSidebar
